import { useState, useRef, useEffect } from 'react';
import { useNotificationStore } from '../stores/notificationStore';
import * as styles from '../styles/components/report-element-menu.css.ts';

interface ReportElementMenuProps {
  elementId: string;
  elementType: string;
  onDeleteElement: (elementId: string) => void;
  onEditElement?: (elementId: string) => void;
  onDuplicateElement?: (elementId: string) => void;
  onOpenSourceDataset?: (elementId: string) => void;
  onExportElement?: (elementId: string) => void;
  onResizeElement?: (elementId: string) => void;
  hasDatasetId?: boolean;
  /** レポート内でこの要素が参照されている回数 */
  referenceCount?: number;
  /** メニューの表示コンテキスト（data-testidの一意性確保用） */
  context?: 'inline' | 'list';
}

export function ReportElementMenu({
  elementId,
  elementType,
  onDeleteElement,
  onEditElement,
  onDuplicateElement,
  onOpenSourceDataset,
  onExportElement,
  onResizeElement,
  hasDatasetId = false,
  referenceCount = 1,
  context
}: ReportElementMenuProps) {
  // data-testid用のプレフィックス（contextが指定されている場合のみ付与）
  const testIdPrefix = context ? `${context}-` : '';
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { warning: showWarning } = useNotificationStore();

  // メニューの外側をクリックした時に閉じる
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleMenuItemClick = (action: () => void) => {
    // 複数参照されている場合は警告を表示
    if (referenceCount > 1) {
      showWarning(
        `This element is referenced ${referenceCount} times in the report. Changes will affect all references.`,
        { duration: undefined, dismissible: true }
      );
    }
    action();
    setIsOpen(false);
  };

  return (
    <div className={`${styles.menuContainer} no-export`}>
      <button
        ref={buttonRef}
        className={styles.menuButton}
        onClick={() => setIsOpen(!isOpen)}
        title="Element options"
        aria-label="Report element menu"
        data-testid={`${testIdPrefix}element-menu-button-${elementId}`}
      >
        ...
      </button>

      {isOpen && (
        <div ref={menuRef} className={styles.menuDropdown}>
          {/* Edit button - for editable elements */}
          {onEditElement && elementType !== 'text_memo' && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(() => onEditElement(elementId))}
                data-testid={`${testIdPrefix}edit-element-${elementId}`}
              >
                View settings
              </button>
              <div className={styles.menuDivider} />
            </>
          )}

          {/* Export button - only for graph_builder elements */}
          {onExportElement && elementType === 'graph_builder' && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(() => onExportElement(elementId))}
                data-testid={`${testIdPrefix}export-element-${elementId}`}
              >
                Export
              </button>
              <div className={styles.menuDivider} />
            </>
          )}

          {/* Open Source Dataset button */}
          {onOpenSourceDataset && hasDatasetId && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(() => onOpenSourceDataset(elementId))}
                data-testid={`${testIdPrefix}open-dataset-${elementId}`}
              >
                Open Source Dataset
              </button>
              <div className={styles.menuDivider} />
            </>
          )}

          {/* Duplicate button */}
          {onDuplicateElement && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(() => onDuplicateElement(elementId))}
                data-testid={`${testIdPrefix}duplicate-element-${elementId}`}
              >
                Duplicate
              </button>
              <div className={styles.menuDivider} />
            </>
          )}

          {/* Resize button */}
          {onResizeElement && (
            <>
              <button
                className={styles.menuItem}
                onClick={() => handleMenuItemClick(() => onResizeElement(elementId))}
                data-testid={`${testIdPrefix}resize-element-${elementId}`}
              >
                Resize
              </button>
              <div className={styles.menuDivider} />
            </>
          )}

          <button
            className={styles.menuItem}
            onClick={() => handleMenuItemClick(() => onDeleteElement(elementId))}
            data-testid={`${testIdPrefix}delete-element-${elementId}`}
          >
            Delete
          </button>
        </div>
      )}
    </div>
  );
}
